import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { InputFormGroup, LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { ValidationGroup, Input, Checkbox } from '@bingads-webui-react/primitive-validation';
import { Constants, TaxUtils, CountryUtils } from '@bingads-webui-clientcenter/entity-utils';
import { InlineInfoAlert } from '@bingads-webui-cc-react/alert';
import { EmptyTaxForm } from './empty-taxform';
import { EmptyViewTaxForm } from './viewtaxforms/empty-viewtaxform';
import { TaxTypeFormPropTypes } from '../shapes';
import {
  InputTaxFieldId,
  IsTaxExemptionCertVerifiedTypeKey,
  IsTaxExemptionCertVerifiedLabelKey,
  SkipVatIdValidationTypeKey,
  SkipVatIdValidationLabelKey,
  InvalidVatIDKey,
  MaxLengthExceededKey,
  LegalIdentifierNumberPilot,
  LegalIdentifierHintI18nKey,
  TaxFormMode,
  requiredFeatures,
} from '../constants';
import { registerTaxform } from '../taxform-store';
import {
  withViewTaxFormWithFilledEmptyTaxValue,
  getHelpPopupTopicFromTaxSchema,
  isTaxExemptionCertVerified,
  getPilotStatus,
  addLegalIdentifierNumberInTaxSchema,
  onTaxFormValidateFillingLegalIdentifier,
  getTaxTypeLabel,
  withTaxFormByPilot,
} from '../utils';
import { CustomValidateItems } from '../validator';
import { LegalIdentifierNumberField } from '../controls';
import { useValidationI18n } from '../hooks/use-i18n';

const {
  TaxLabelFormat, TaxType, TaxExemptionCertVerifiedResult, CountryCode,
} = Constants;
const EMEAMaxLength = 50;
const checkboxConvertor = {
  fromModel: isTaxExemptionCertVerified,
  toModel: value => (value ? TaxExemptionCertVerifiedResult.Accept : TaxExemptionCertVerifiedResult.Reject),
};
export const countryVatRegexes = {
  AT: /^ATU\d{8}$/,
  BE: /^BE\d{10}$/,
  BG: /^BG\d{9,10}$/,
  HR: /^HR\d{11}$/,
  HU: /^HU\d{8}$/,
  CZ: /^CZ\d{8,10}$/,
  DK: /^DK\d{8}$/,
  EE: /^EE\d{9}$/,
  FI: /^FI\d{8}$/,
  GP: /^FR[A-Z0-9]{2}\d{9}$/,
  FR: /^FR[A-Z0-9]{2}\d{9}$/,
  DE: /^DE\d{9}$/,
  GR: /^EL\d{9}$/,
  IE: /^IE(\d{7}[A-Z]{1,2}$|\d[A-Z]\d{5}[A-Z]$)/,
  IT: /^IT\d{11}$/,
  LV: /^LV\d{11}$/,
  LI: /^(LI)?\d{5}$/,
  LT: /^LT(\d{12}|\d{9})$/,
  LU: /^LU\d{8}$/,
  MT: /^MT\d{8}$/,
  MC: /^FR[A-Z0-9]{2}\d{9}$/,
  NL: /^NL\d{9}B\d{2}$/,
  PL: /^PL\d{10}$/,
  PT: /^PT\d{9}$/,
  SK: /^SK\d{10}$/,
  MQ: /^FR[A-Z0-9]{2}\d{9}$/,
  SI: /^SI\d{8}$/,
  ES: /^ES([A-Z]\d{7}[A-Z0-9]|\d{8}[A-Z])$/,
  SE: /^SE\d{12}$/,
  CH: /^CHE-?\d{3}\.?\d{3}\.?\d{3}(\s?(MWST|TVA|IVA))?$/,
  GB: /^GB(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/,
  CY: /^CY\d{8}[A-Z]$/,
  RO: /^RO\d{2,10}$/,
  RE: /^FR[A-Z0-9]{2}\d{9}$/,
};
const customFormatErrorMessageKeys = {
  MQ: CustomValidateItems.MartiniqueVATFormatValidation,
  HU: CustomValidateItems.HungaryVATFormatValidation,
  GP: CustomValidateItems.GuadeloupeVATFormatValidation,
  PT: CustomValidateItems.PortugalVATFormatValidation,
};

const CountriesWithLegalIdentifierNumber = [CountryCode.HU, CountryCode.PL];
const CountriesWithLegalIdentifierNumberVisuallyHidden = [CountryCode.HU];

const shouldShowLegalIdentifierNumberField = ({ permissions, countryCode }) => {
  const IsLegalIdentifierNumberPilotOn = getPilotStatus(LegalIdentifierNumberPilot, permissions);
  return IsLegalIdentifierNumberPilotOn && _.indexOf(CountriesWithLegalIdentifierNumber, countryCode) > -1;
};

const shouldVisuallyHideLegalIdentifierNumberField = ({ permissions, countryCode }) => {
  const IsLegalIdentifierNumberPilotOn = getPilotStatus(LegalIdentifierNumberPilot, permissions);
  return IsLegalIdentifierNumberPilotOn && _.indexOf(CountriesWithLegalIdentifierNumberVisuallyHidden, countryCode) > -1;
};

const getEMEATaxSchema = ({
  isSignupFlow, permissions, countryCode, taxFormMode,
}) => {
  const taxSchema = {
    name: 'EMEA Taxation Validation Schema',
    type: 'object',
    properties: {
      [TaxType.VatNumber]: {
        type: 'string',
        maxLength: EMEAMaxLength,
        helpPopupTopic: isSignupFlow ? 'Pop_BA_Signup_VATNumber' : 'Pop_BA_Accounts_VATNumber',
      },
      [SkipVatIdValidationTypeKey]: {
        type: 'string',
      },
      [IsTaxExemptionCertVerifiedTypeKey]: {
        type: 'string',
      },
    },
  };

  if (shouldShowLegalIdentifierNumberField({ permissions, countryCode })
    && !(taxFormMode === TaxFormMode.ViewMode && shouldVisuallyHideLegalIdentifierNumberField({ permissions, countryCode }))) {
    addLegalIdentifierNumberInTaxSchema(taxSchema);
  }

  return taxSchema;
};

const EMEATaxForm = (props) => {
  const {
    showLineByLineFormGroup,
    i18n,
    disabled,
    onValidate,
    data,
    alwaysShowError,
    customVATValidator,
    countries,
    countryCode,
    isVatRequired,
    showTaxApprovalForm,
    permissions,
  } = props;
  const validationI18n = useValidationI18n(i18n);
  const CustomizedFormGroup = showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;
  const countryName = (CountryUtils.findCountry({ countries, countryCode }) || {}).Name;
  const taxSchema = getEMEATaxSchema(props);
  const taxLabelFormat = isVatRequired ? TaxLabelFormat.Required : TaxLabelFormat.RequiredForVATRegisteredBusiness;
  const showLegalIdentifierNumberField = shouldShowLegalIdentifierNumberField({ permissions, countryCode });
  const legalIdentifierNumberFieldVisuallyHidden = shouldVisuallyHideLegalIdentifierNumberField({ permissions, countryCode });

  const onTaxFormValidate = showLegalIdentifierNumberField
    ? newTaxData => onTaxFormValidateFillingLegalIdentifier({
      newTaxData,
      onValidate,
      prevTaxData: data,
      listenedToField: TaxType.VatNumber,
      visuallyHidden: legalIdentifierNumberFieldVisuallyHidden,
    })
    : onValidate;

  return (
    <ValidationGroup
      validationProps={{
        schema: taxSchema,
        i18n: validationI18n,
        onValidate: onTaxFormValidate,
        data,
      }}
    >
      <CustomizedFormGroup
        isStatic
        label={getTaxTypeLabel(TaxType.VatNumber, i18n, { labelFormat: taxLabelFormat })}
        helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema, taxType: TaxType.VatNumber })}
      >
        <Input
          id={InputTaxFieldId(TaxType.VatNumber)}
          validationProps={{
            field: TaxType.VatNumber,
            alwaysShowError,
            validator: customVATValidator,
          }}
          disabled={disabled}
        />
        {
          isVatRequired
            ? <InlineInfoAlert>{i18n.getString(_TL_('For accounts with business locations in {{countryName}} we require a valid VAT ID.'), { countryName })}</InlineInfoAlert>
            : <InlineInfoAlert>{i18n.getString(_TL_('Failure to provide your VAT registration number may result in tax being applied to your invoice.'))}</InlineInfoAlert>
        }
      </CustomizedFormGroup>
      {showTaxApprovalForm && (
        <React.Fragment>
          <Checkbox id={`tax-checkbox-${IsTaxExemptionCertVerifiedTypeKey}`} validationProps={{ field: IsTaxExemptionCertVerifiedTypeKey, convertor: checkboxConvertor }} disabled={disabled} >{i18n.getString(IsTaxExemptionCertVerifiedLabelKey)}</Checkbox>
          <Checkbox id={`tax-checkbox-${SkipVatIdValidationTypeKey}`} validationProps={{ field: SkipVatIdValidationTypeKey, convertor: checkboxConvertor }} disabled={disabled} >{i18n.getString(SkipVatIdValidationLabelKey)}</Checkbox>
        </React.Fragment>
      )}
      {showLegalIdentifierNumberField &&
        <LegalIdentifierNumberField
          i18n={i18n}
          hintContent={i18n.getString(LegalIdentifierHintI18nKey[countryCode])}
          showLineByLineFormGroup
          alwaysShowError={alwaysShowError}
          disabled={disabled}
          visuallyHidden={legalIdentifierNumberFieldVisuallyHidden}
        />
      }
    </ValidationGroup>
  );
};

EMEATaxForm.propTypes = {
  ...TaxTypeFormPropTypes,
  customVATValidator: PropTypes.func,
};
EMEATaxForm.defaultProps = {
  customVATValidator: null,
};

const GetEMEACountryTaxForm = ({ countryVatRegex, countryCode }) => {
  const EMEACountryTaxForm = (props) => {
    const { i18n, permissions } = props;
    const isVatRequired = TaxUtils.isEMEAVatIdRequired({ permissions, countryCode });

    const vatValidator = useCallback(
      (value) => {
        const isVatEmpty = !value || value.length === 0;

        if (isVatRequired || !isVatEmpty) {
          if (value.length > EMEAMaxLength) {
            return [i18n.getString(MaxLengthExceededKey, { maxlength: EMEAMaxLength })];
          }

          if (!countryVatRegex.test(value)) {
            if (_.has(customFormatErrorMessageKeys, countryCode)) {
              return [i18n.getString(customFormatErrorMessageKeys[countryCode])];
            }

            return [i18n.getString(InvalidVatIDKey)];
          }
        }

        return undefined;
      },
      [i18n, isVatRequired]
    );

    return <EMEATaxForm {...props} customVATValidator={vatValidator} countryCode={countryCode} isVatRequired={isVatRequired} />;
  };
  EMEACountryTaxForm.propTypes = TaxTypeFormPropTypes;

  return EMEACountryTaxForm;
};

const getEMEAViewTaxForm = ({ countryCode }) => withViewTaxFormWithFilledEmptyTaxValue({ getTaxSchema: props => getEMEATaxSchema({ countryCode, ...props }) });

const countryPilotMapping = {
  MQ: requiredFeatures.ABLProjectEmeraldTaxCollection,
  GP: requiredFeatures.ABLProjectEmeraldTaxCollection,
  RE: requiredFeatures.ABLReunionTax,
};

_.each(countryVatRegexes, (countryVatRegex, countryCode) => {
  if (countryCode in countryPilotMapping) {
    // use pilot control if any
    const pilotFlag = countryPilotMapping[countryCode];
    const taxform = withTaxFormByPilot({ pilotFlag, PilotForm: GetEMEACountryTaxForm({ countryVatRegex, countryCode }), DefaultForm: EmptyTaxForm });
    const ViewTaxForm = withTaxFormByPilot({ pilotFlag, PilotForm: getEMEAViewTaxForm({ countryCode }), DefaultForm: EmptyViewTaxForm });

    registerTaxform(`EMEA/${countryCode}`, taxform, ViewTaxForm);
  } else {
    registerTaxform(`EMEA/${countryCode}`, GetEMEACountryTaxForm({ countryVatRegex, countryCode }), getEMEAViewTaxForm({ countryCode }));
  }
});

registerTaxform('EMEA/', EMEATaxForm, withViewTaxFormWithFilledEmptyTaxValue({ getTaxSchema: getEMEATaxSchema }));
